import { forwardRef } from 'react';
import { Box } from '@terra/ui-styled-system';
import { Text } from './text';
import type { StyledMetricItemProps } from './styled/metric-item';
import { StyledMetricItem } from './styled/metric-item';
import type { FindingSeverityType } from '@terra/shared-schemas-orchestrator';
import { SeverityIndicator } from './severity-indicator';
import { Tooltip } from './tooltip';
import { useTranslations } from 'next-intl';
import { useNumberFormatter } from '@terra/ui-formatters';

type Severity = Exclude<FindingSeverityType, 'Info'>;

export interface MetricItemProps extends StyledMetricItemProps {
  icon?: React.ReactNode;
  severity?: Severity;
  label: string;
  metrics: number;
  newMetrics?: number;
  disabled?: boolean;
  hideTooltip?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  tooltipCustomContent?: React.ReactNode;
}

export const MetricItem = forwardRef<HTMLButtonElement, MetricItemProps>(
  (
    {
      icon,
      severity,
      label,
      metrics,
      newMetrics,
      disabled = false,
      hideTooltip = false,
      onClick,
      tooltipCustomContent,
      ...rest
    },
    ref,
  ) => {
    const t = useTranslations('designSystem.metricItem');
    const numberFormatter = useNumberFormatter();
    const tooltipTitle = severity
      ? t('findingTooltip', {
          count: metrics,
          severity,
        })
      : `${numberFormatter.format(metrics)} ${label}`;
    const tooltipSubtitle =
      newMetrics !== undefined && newMetrics > 0
        ? t('newSinceLastVisit', { count: newMetrics })
        : undefined;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClick?.(event);
    };

    const shouldHaveActiveStyle = onClick && !disabled && !severity;

    const metricButton = (
      <StyledMetricItem
        ref={ref}
        disabled={disabled}
        onClick={onClick ? handleClick : undefined}
        _hover={{ bg: shouldHaveActiveStyle ? 'bg.muted' : 'transparent' }}
        _active={
          shouldHaveActiveStyle
            ? {
                borderWidth: '1px',
                borderColor: 'border.muted',
                bg: 'bg.muted',
              }
            : undefined
        }
        {...rest}
      >
        <Box display="inline-flex" color={disabled ? 'fg.disabled' : 'inherit'}>
          {severity ? <SeverityIndicator severity={severity} /> : icon}
        </Box>
        <Text as="span">{numberFormatter.format(metrics)}</Text>
        {newMetrics !== undefined && newMetrics > 0 && (
          <Box
            position="absolute"
            top={-1}
            right={-1}
            width={2}
            height={2}
            borderRadius="full"
            bg="fg.severity.critical"
          />
        )}
      </StyledMetricItem>
    );

    if (hideTooltip) {
      return metricButton;
    }

    return (
      <Tooltip
        title={tooltipCustomContent ? undefined : tooltipTitle}
        subtitle={tooltipCustomContent ? undefined : tooltipSubtitle}
        customContent={tooltipCustomContent}
      >
        {metricButton}
      </Tooltip>
    );
  },
);

MetricItem.displayName = 'MetricItem';
